/**
 * Layout-mode resolver (q-portrait-hud-and-orientation-settings, AD-817; design
 * `docs/design/mobile-layout.md` §2). Pure: given the viewport, whether the primary
 * pointer is coarse (touch) and the player's Orientation setting, decide which HUD
 * arrangement draws — `desktop` (the original PXL layout), `portrait` (playfield-first:
 * a top status strip, one bottom action row, panels as bottom sheets) or `landscape`
 * (a short-height phone held sideways: the same compact pieces, panels as a side sheet).
 *
 * The result is written to `<html data-layout="…">` by `LayoutController`; every
 * reflow is CSS keyed on that attribute (`ui/layout/mobile-layout.css`).
 */

export type OrientationPref = 'auto' | 'portrait' | 'landscape';
export type LayoutMode = 'desktop' | 'portrait' | 'landscape';

export const ORIENTATION_PREFS: readonly OrientationPref[] = ['auto', 'portrait', 'landscape'];

export interface LayoutInput {
  /** CSS px (window.innerWidth). */
  viewportWidth: number;
  /** CSS px (window.innerHeight). */
  viewportHeight: number;
  /** `(pointer: coarse)` — a touch-first device. */
  coarsePointer: boolean;
  /** The player's Orientation setting. */
  orientationPref: OrientationPref;
}

/**
 * A short side under this is a phone in either orientation (390, 412, 430 are the common
 * widths), so the compact layouts apply whatever the pointer says.
 */
export const PHONE_SHORT_SIDE_PX = 520;
/** A portrait viewport narrower than this cannot hold the desktop HUD's 260 px side columns. */
export const NARROW_PORTRAIT_PX = 700;
/** A coarse-pointer landscape viewport shorter than this gets the compact landscape layout. */
export const TOUCH_SHORT_HEIGHT_PX = 600;
/** Any landscape viewport shorter than this does (a short desktop window included). */
export const SHORT_HEIGHT_PX = 480;

/** True when the viewport is a handheld's: touch-first or a phone-sized short side. */
export function isCompactDevice(
  input: Pick<LayoutInput, 'viewportWidth' | 'viewportHeight' | 'coarsePointer'>,
): boolean {
  const shortSide = Math.min(input.viewportWidth, input.viewportHeight);
  return input.coarsePointer || shortSide < PHONE_SHORT_SIDE_PX;
}

/** The arrangement the viewport alone asks for (the `auto` setting). */
export function autoLayout(input: Omit<LayoutInput, 'orientationPref'>): LayoutMode {
  const w = sane(input.viewportWidth);
  const h = sane(input.viewportHeight);
  if (w === 0 || h === 0) return 'desktop';
  const compact = isCompactDevice({
    viewportWidth: w,
    viewportHeight: h,
    coarsePointer: input.coarsePointer,
  });
  if (h > w) return compact || w < NARROW_PORTRAIT_PX ? 'portrait' : 'desktop';
  if (compact && h < TOUCH_SHORT_HEIGHT_PX) return 'landscape';
  if (h < SHORT_HEIGHT_PX) return 'landscape';
  return 'desktop';
}

/**
 * The arrangement that draws. A lock (`portrait` / `landscape`) holds the in-game layout
 * whichever way the device is turned — the browser's own screen rotation is only stopped
 * where `screen.orientation.lock` is honoured (`orientationLock.ts`). A `landscape` lock on
 * a large, fine-pointer screen is the desktop layout: that IS its landscape arrangement.
 */
export function resolveLayout(input: LayoutInput): LayoutMode {
  switch (input.orientationPref) {
    case 'portrait':
      return 'portrait';
    case 'landscape': {
      const auto = autoLayout(input);
      if (auto === 'landscape' || auto === 'desktop') return auto;
      // Auto said portrait (the device is upright): keep the sideways arrangement for a
      // handheld, the desktop one for a large screen.
      return isCompactDevice(input) ? 'landscape' : 'desktop';
    }
    case 'auto':
    default:
      return autoLayout(input);
  }
}

/** Compact layouts collapse the minimap / chat / launcher by default (playfield first). */
export function isCompactLayout(mode: LayoutMode): boolean {
  return mode !== 'desktop';
}

export function parseOrientationPref(raw: string | null | undefined): OrientationPref {
  return raw === 'portrait' || raw === 'landscape' ? raw : 'auto';
}

function sane(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}
